import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Github, 
  Linkedin, 
  Twitter, 
  Youtube, 
  Mail,
  ExternalLink,
  Terminal,
  BookOpen,
  Award,
  Briefcase,
  FileText,
  Brain,
  ChevronRight,
  Calendar,
  Clock,
  ArrowRight,
  BookMarked,
  Star
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Typewriter component
const Typewriter = ({ text, delay = 50 }) => {
  const [displayText, setDisplayText] = useState('');
  const [showCursor, setShowCursor] = useState(true);

  useEffect(() => {
    let i = 0;
    const timer = setInterval(() => {
      if (i < text.length) {
        setDisplayText(text.slice(0, i + 1));
        i++;
      } else {
        clearInterval(timer);
      }
    }, delay);
    return () => clearInterval(timer);
  }, [text, delay]);

  useEffect(() => {
    const cursorTimer = setInterval(() => {
      setShowCursor(prev => !prev);
    }, 530);
    return () => clearInterval(cursorTimer);
  }, []);

  return (
    <span className="font-mono">
      {displayText}
      <span className={`${showCursor ? 'opacity-100' : 'opacity-0'} text-primary`}>▊</span>
    </span>
  );
};

export const Home = () => {
  const { isAuthenticated, isAdmin } = useAuth();
  const [posts, setPosts] = useState([]);
  const [journals, setJournals] = useState([]);
  const [projects, setProjects] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [books, setBooks] = useState([]);
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    // Fetch settings and content
    const fetchData = async () => {
      try {
        const [settingsRes, postsRes, journalsRes, projectsRes, certsRes, booksRes] = await Promise.all([
          fetch(`${API}/settings`),
          fetch(`${API}/posts?limit=3`),
          fetch(`${API}/journals?limit=5`),
          fetch(`${API}/projects?limit=4`),
          fetch(`${API}/certificates?limit=4`),
          fetch(`${API}/books?limit=6`)
        ]);
        
        if (settingsRes.ok) {
          const settingsData = await settingsRes.json();
          setSettings(settingsData);
          // Apply custom primary color if set
          if (settingsData.primary_color) {
            document.documentElement.style.setProperty('--primary', settingsData.primary_color);
          }
        }
        if (postsRes.ok) setPosts(await postsRes.json());
        if (journalsRes.ok) setJournals(await journalsRes.json());
        if (projectsRes.ok) setProjects(await projectsRes.json());
        if (certsRes.ok) setCertificates(await certsRes.json());
        if (booksRes.ok) setBooks(await booksRes.json());
      } catch (error) {
        console.log('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-3 h-3 ${star <= (rating || 0) ? 'fill-yellow-500 text-yellow-500' : 'text-muted-foreground'}`}
          />
        ))}
      </div>
    );
  };

  const statusLabels = {
    reading: { label: 'Reading', color: 'bg-blue-500/10 text-blue-500 border-blue-500/20' },
    completed: { label: 'Completed', color: 'bg-green-500/10 text-green-500 border-green-500/20' },
    want_to_read: { label: 'Want to Read', color: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20' }
  };

  // Use settings or defaults
  const socialLinks = settings ? [
    { icon: Github, href: settings.github_url, label: 'GitHub' },
    { icon: Linkedin, href: settings.linkedin_url, label: 'LinkedIn' },
    { icon: Twitter, href: settings.twitter_url, label: 'Twitter' },
    { icon: Youtube, href: settings.youtube_url, label: 'YouTube' },
    { icon: Mail, href: `mailto:${settings.email}`, label: 'Email' },
  ].filter(l => l.href) : [];

  const stats = settings?.stats || [
    { label: 'AI Models Built', value: '15+' },
    { label: 'Papers Read', value: '200+' },
    { label: 'Articles Written', value: '50+' },
    { label: 'Open Source', value: '10+' },
  ];

  const skills = settings?.skills || [
    'LLMs', 'PyTorch', 'Transformers', 'RAG', 'Fine-tuning', 
    'Langchain', 'CUDA', 'MLOps', 'Python', 'FastAPI'
  ];

  const profileName = settings?.name || 'geekyrahul';
  const profileTitle = settings?.title || 'Senior AI Engineer';
  const profileTagline = settings?.tagline || 'We4AI: Let\'s learn AI together';
  const profileBio = settings?.bio || 'Building intelligent systems at the intersection of LLMs, Neural Networks, and Production ML.';
  const profileImage = settings?.profile_image || 'https://customer-assets-wrfwihn1.emergentagent.net/job_knowledge-portal-60/artifacts/raiejekg_Untitled%20%28500%20x%20600%20px%29%20%28300%20x%20600%20px%29%20%281%29.png';

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 grid-pattern opacity-50" />
        <div className="relative max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-5 gap-12 items-center">
            {/* Left Content - 3 cols */}
            <div className="lg:col-span-3 space-y-8 animate-fade-in">
              {/* Terminal prompt */}
              <div className="inline-flex items-center gap-2 bg-card border border-border rounded-lg px-4 py-2 font-mono text-sm">
                <Terminal className="w-4 h-4 text-primary" />
                <span className="text-muted-foreground">$</span>
                <Typewriter text="python train.py --model gpt-we4ai --mode learn" delay={40} />
              </div>

              {/* Main heading */}
              <div className="space-y-4">
                <p className="mono-label">{profileTitle}</p>
                <h1 className="text-4xl lg:text-5xl font-bold tracking-tight">
                  Hey, I'm <span className="gradient-text">{profileName}</span>
                </h1>
                <p className="text-xl text-muted-foreground max-w-xl leading-relaxed">
                  {profileBio}
                </p>
              </div>

              {/* Tagline */}
              <div className="flex items-center gap-3">
                <Brain className="w-5 h-5 text-primary" />
                <span className="text-lg font-medium">{profileTagline}</span>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-wrap gap-4 pt-4">
                <Link to="/articles">
                  <Button size="lg" className="rounded-lg h-12 px-6" data-testid="cta-articles">
                    <BookOpen className="w-4 h-4 mr-2" />
                    Read Articles
                  </Button>
                </Link>
                <Link to="/projects">
                  <Button size="lg" variant="outline" className="rounded-lg h-12 px-6" data-testid="cta-projects">
                    <Briefcase className="w-4 h-4 mr-2" />
                    View Projects
                  </Button>
                </Link>
                {isAdmin && (
                  <Link to="/admin">
                    <Button size="lg" variant="secondary" className="rounded-lg h-12 px-6" data-testid="cta-admin">
                      Admin Panel
                    </Button>
                  </Link>
                )}
              </div>

              {/* Social Links */}
              <div className="flex items-center gap-4 pt-4">
                {socialLinks.map((link) => (
                  <a
                    key={link.label}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 rounded-lg bg-secondary hover:bg-secondary/80 text-muted-foreground hover:text-foreground transition-colors"
                    data-testid={`social-${link.label.toLowerCase()}`}
                  >
                    <link.icon className="w-5 h-5" />
                  </a>
                ))}
              </div>
            </div>

            {/* Right - Profile Image - 2 cols */}
            <div className="lg:col-span-2 flex justify-center lg:justify-end">
              <div className="relative">
                {/* Gradient background blob */}
                <div className="absolute -inset-4 bg-gradient-to-br from-primary/20 via-primary/5 to-transparent rounded-full blur-3xl" />
                {/* Image container */}
                <div className="relative">
                  <img
                    src={profileImage}
                    alt={`${profileName} - ${profileTitle}`}
                    className="w-64 lg:w-72 h-auto object-contain drop-shadow-2xl"
                    data-testid="profile-image"
                  />
                  {/* Decorative elements */}
                  <div className="absolute -bottom-4 -left-4 w-24 h-24 border-2 border-primary/30 rounded-lg -z-10" />
                  <div className="absolute -top-4 -right-4 w-16 h-16 border-2 border-primary/20 rounded-full -z-10" />
                </div>
              </div>
            </div>
          </div>

          {/* Stats - below the grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12 animate-fade-in">
            {stats.map((stat) => (
              <div key={stat.label} className="bg-card border border-border rounded-lg p-4">
                <p className="text-2xl font-bold text-primary">{stat.value}</p>
                <p className="text-sm text-muted-foreground">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-card/50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="space-y-8">
            <div>
              <p className="mono-label mb-2">About</p>
              <h2 className="text-2xl lg:text-3xl font-semibold tracking-tight">
                Building the future with AI
              </h2>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="md:col-span-2 space-y-4 text-muted-foreground leading-relaxed">
                <p>
                  I'm a Senior AI Engineer passionate about pushing the boundaries of what's possible 
                  with artificial intelligence. My journey spans from building production-grade ML systems 
                  to contributing to open-source AI projects.
                </p>
                <p>
                  Currently focused on Large Language Models, Retrieval-Augmented Generation, and 
                  making AI more accessible to everyone. I believe in learning in public and sharing 
                  knowledge with the community.
                </p>
                <p>
                  Through <span className="text-primary font-medium">We4AI</span>, I document my learnings, 
                  share tutorials, and build tools that help others on their AI journey.
                </p>
              </div>
              
              <div className="space-y-4">
                <p className="text-sm font-medium text-foreground">Tech Stack</p>
                <div className="flex flex-wrap gap-2">
                  {skills.map((skill) => (
                    <Badge key={skill} variant="secondary" className="font-mono text-xs">
                      {skill}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Recent Articles */}
      <section id="articles" className="py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="mono-label mb-2">Articles</p>
              <h2 className="text-2xl lg:text-3xl font-semibold tracking-tight">
                Latest writings
              </h2>
            </div>
            <Link to="/articles">
              <Button variant="ghost" className="text-primary" data-testid="view-all-articles">
                View all <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>

          <div className="space-y-4 stagger">
            {posts.length > 0 ? posts.map((post) => (
              <Link 
                key={post.post_id} 
                to={`/articles/${post.post_id}`}
                className="block"
                data-testid={`article-${post.post_id}`}
              >
                <div className="group bg-card border border-border rounded-lg p-6 card-glow">
                  <div className="flex items-start justify-between gap-4">
                    <div className="space-y-2">
                      <h3 className="text-lg font-medium group-hover:text-primary transition-colors">
                        {post.title}
                      </h3>
                      <p className="text-muted-foreground line-clamp-2">{post.excerpt}</p>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(post.created_at).toLocaleDateString()}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {post.read_time || '5 min'}
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                </div>
              </Link>
            )) : (
              <div className="bg-card border border-border rounded-lg p-8 text-center">
                <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Articles coming soon...</p>
                {isAdmin && (
                  <Link to="/admin">
                    <Button variant="link" className="mt-2">Add your first article</Button>
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Learning Journal Preview */}
      <section id="journal" className="py-20 bg-card/50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="mono-label mb-2">Journal</p>
              <h2 className="text-2xl lg:text-3xl font-semibold tracking-tight">
                Daily learnings
              </h2>
            </div>
            <Link to="/journal">
              <Button variant="ghost" className="text-primary" data-testid="view-all-journal">
                View all <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>

          <div className="relative border-l-2 border-primary/20 ml-4 pl-6 space-y-6 stagger">
            {journals.length > 0 ? journals.map((entry) => (
              <div key={entry.journal_id} className="relative" data-testid={`journal-${entry.journal_id}`}>
                <div className="timeline-dot" />
                <div className="bg-card border border-border rounded-lg p-4 card-glow">
                  <p className="text-sm text-muted-foreground mb-2">
                    {new Date(entry.created_at).toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </p>
                  <p className="text-foreground">{entry.content}</p>
                  {entry.tags && entry.tags.length > 0 && (
                    <div className="flex gap-2 mt-3">
                      {entry.tags.map((tag) => (
                        <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )) : (
              <div className="bg-card border border-border rounded-lg p-8 text-center">
                <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Journal entries coming soon...</p>
                {isAdmin && (
                  <Link to="/admin">
                    <Button variant="link" className="mt-2">Add your first entry</Button>
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Projects Preview */}
      <section id="projects" className="py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="mono-label mb-2">Projects</p>
              <h2 className="text-2xl lg:text-3xl font-semibold tracking-tight">
                Featured work
              </h2>
            </div>
            <Link to="/projects">
              <Button variant="ghost" className="text-primary" data-testid="view-all-projects">
                View all <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>

          <div className="grid md:grid-cols-2 gap-6 stagger">
            {projects.length > 0 ? projects.map((project) => (
              <div 
                key={project.project_id} 
                className="bg-card border border-border rounded-lg p-6 card-glow"
                data-testid={`project-${project.project_id}`}
              >
                <h3 className="text-lg font-medium mb-2">{project.title}</h3>
                <p className="text-muted-foreground text-sm mb-4 line-clamp-2">{project.description}</p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {project.tech_stack?.slice(0, 4).map((tech) => (
                    <Badge key={tech} variant="secondary" className="text-xs font-mono">{tech}</Badge>
                  ))}
                </div>
                <div className="flex gap-3">
                  {project.github_url && (
                    <a href={project.github_url} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground">
                      <Github className="w-4 h-4" />
                    </a>
                  )}
                  {project.demo_url && (
                    <a href={project.demo_url} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}
                </div>
              </div>
            )) : (
              <div className="md:col-span-2 bg-card border border-border rounded-lg p-8 text-center">
                <Briefcase className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Projects coming soon...</p>
                {isAdmin && (
                  <Link to="/admin">
                    <Button variant="link" className="mt-2">Add your first project</Button>
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Certificates Preview */}
      <section id="certificates" className="py-20 bg-card/50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="mono-label mb-2">Certificates</p>
              <h2 className="text-2xl lg:text-3xl font-semibold tracking-tight">
                Achievements
              </h2>
            </div>
            <Link to="/certificates">
              <Button variant="ghost" className="text-primary" data-testid="view-all-certs">
                View all <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>

          <div className="grid md:grid-cols-2 gap-6 stagger">
            {certificates.length > 0 ? certificates.map((cert) => (
              <div 
                key={cert.cert_id} 
                className="bg-card border border-border rounded-lg p-6 card-glow"
                data-testid={`cert-${cert.cert_id}`}
              >
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-primary/10 rounded-lg">
                    <Award className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium">{cert.title}</h3>
                    <p className="text-sm text-muted-foreground">{cert.issuer}</p>
                    <p className="text-xs text-muted-foreground mt-1">{cert.date}</p>
                  </div>
                </div>
              </div>
            )) : (
              <div className="md:col-span-2 bg-card border border-border rounded-lg p-8 text-center">
                <Award className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Certificates coming soon...</p>
                {isAdmin && (
                  <Link to="/admin">
                    <Button variant="link" className="mt-2">Add your first certificate</Button>
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Books/Reading List Preview */}
      <section id="books" className="py-20">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="mono-label mb-2">Reading List</p>
              <h2 className="text-2xl lg:text-3xl font-semibold tracking-tight">
                Books I'm Reading
              </h2>
            </div>
            <Link to="/books">
              <Button variant="ghost" className="text-primary" data-testid="view-all-books">
                View all <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 stagger">
            {books.length > 0 ? books.map((book) => (
              <div 
                key={book.book_id} 
                className="bg-card border border-border rounded-lg p-4 card-glow"
                data-testid={`book-${book.book_id}`}
              >
                <div className="flex gap-4">
                  {book.cover_url ? (
                    <img src={book.cover_url} alt={book.title} className="w-16 h-24 object-cover rounded shrink-0" />
                  ) : (
                    <div className="w-16 h-24 bg-primary/10 rounded flex items-center justify-center shrink-0">
                      <BookMarked className="w-6 h-6 text-primary" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <h3 className="font-medium text-sm line-clamp-2">{book.title}</h3>
                    <p className="text-xs text-muted-foreground mt-1">by {book.author}</p>
                    <Badge variant="outline" className={`text-xs mt-2 ${statusLabels[book.status]?.color}`}>
                      {statusLabels[book.status]?.label}
                    </Badge>
                    {book.rating && <div className="mt-2">{renderStars(book.rating)}</div>}
                  </div>
                </div>
              </div>
            )) : (
              <div className="md:col-span-2 lg:col-span-3 bg-card border border-border rounded-lg p-8 text-center">
                <BookMarked className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Reading list coming soon...</p>
                {isAdmin && (
                  <Link to="/admin">
                    <Button variant="link" className="mt-2">Add your first book</Button>
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-border">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Brain className="w-5 h-5 text-primary" />
              <span className="font-medium">We4AI</span>
              <span className="text-muted-foreground">· Let's learn AI together</span>
            </div>
            <div className="flex items-center gap-4">
              {socialLinks.map((link) => (
                <a
                  key={link.label}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <link.icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
